import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, MaskedLines } from "./Motion";
import { useSettings } from "@/lib/content";

export function Overline({ children, light = false, className = "" }) {
  return (
    <p className={`flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.28em] ${light ? "text-gold" : "text-gold-deep"} ${className}`}>
      <span className="inline-block h-px w-10 bg-gold" aria-hidden="true" />
      {children}
    </p>
  );
}

export function Btn({ to, href, children, variant = "solid", testid, className = "" }) {
  const base =
    "group inline-flex items-center gap-3 px-7 py-4 text-[11px] font-bold uppercase tracking-[0.22em] transition-all duration-300";
  const styles = {
    solid: "bg-gold text-navy hover:bg-gold-light",
    "outline-light": "border border-ivory/30 text-ivory hover:border-gold hover:text-gold",
    "outline-dark": "border border-navy/25 text-navy hover:border-gold-deep hover:text-gold-deep",
  };
  const cls = `${base} ${styles[variant]} ${className}`;
  const inner = (
    <>
      {children}
      <ArrowRight size={15} strokeWidth={1.75} className="transition-transform duration-300 group-hover:translate-x-1" />
    </>
  );
  if (href)
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testid} className={cls}>
        {inner}
      </a>
    );
  return (
    <Link to={to} data-testid={testid} className={cls}>
      {inner}
    </Link>
  );
}

export function SectionHead({ num, title, text, light = false, className = "" }) {
  return (
    <div className={`max-w-3xl ${className}`}>
      <Reveal>
        <div className="flex items-baseline gap-5">
          <span className="font-display text-sm italic text-gold">{num}</span>
          <span className={`gold-rule w-16 ${light ? "opacity-70" : ""}`} aria-hidden="true" />
        </div>
      </Reveal>
      <Reveal delay={0.08}>
        <h2 className={`mt-5 font-display text-3xl md:text-5xl leading-[1.1] ${light ? "text-ivory" : "text-navy"}`}>{title}</h2>
      </Reveal>
      {text && (
        <Reveal delay={0.16}>
          <p className={`mt-5 max-w-2xl text-base leading-relaxed ${light ? "text-ivory/65" : "text-inkgray"}`}>{text}</p>
        </Reveal>
      )}
    </div>
  );
}

export function PageHero({ title, text, overline }) {
  return (
    <section className="relative overflow-hidden bg-navy pb-20 pt-36 md:pb-28 md:pt-44 grain">
      <span className="pointer-events-none absolute -right-10 top-10 select-none font-display text-[26vw] italic leading-none text-ivory/[0.025]" aria-hidden="true">
        SC
      </span>
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        {overline && <Overline light>{overline}</Overline>}
        <MaskedLines
          text={title}
          lines={2}
          delay={0.15}
          className="mt-6 block font-display text-4xl md:text-6xl leading-[1.08] text-ivory max-w-3xl"
        />
        {text && (
          <Reveal delay={0.5}>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-ivory/65">{text}</p>
          </Reveal>
        )}
      </div>
    </section>
  );
}

export function CTABand({ title, button }) {
  const { settings } = useSettings();
  const c = settings?.contact || {};
  return (
    <section className="bg-navy-2 grain">
      <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-8 px-5 py-16 md:flex-row md:items-center md:px-8 md:py-20">
        <div className="max-w-2xl">
          <Reveal>
            <h2 className="font-display text-3xl md:text-4xl leading-tight text-ivory">{title}</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-4 text-sm text-ivory/60">
              {c.phone && <span>Telefone e WhatsApp: {c.phone}</span>}
              {c.email && <span> • E-mail: {c.email}</span>}
            </p>
          </Reveal>
        </div>
        <Reveal delay={0.15}>
          <Btn to="/contato" variant="solid" testid="cta-band-button">
            {button || "Fale conosco"}
          </Btn>
        </Reveal>
      </div>
    </section>
  );
}

export function usePageTitle(title) {
  if (typeof document !== "undefined") document.title = title ? `${title} — Silva Cotan` : "Silva Cotan — Assessoria Jurídica";
}
